import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import OuterDraggable from "react-draggable";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";
import SelectBox from "../../../images/components/SelectBox";
import { reorderArray } from "../../../../config/utils";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import Portal from "../../../common/components/Portal";

const cloneRows = rows => (rows || []).map(r => ({ ...r }));

const EXPORT_SELECT_OPTIONS = [
    { name: "Yes", id: "yes" },
    { name: "No", id: "no" }
];

const renderExecutiveSummaryCells = ({ row, index, viewOnly, isClone = false, onFieldChange, LOCATION_SELECT_OPTIONS }) => (
    <>
        <td className="bbi-executive-export-td-name">
            <div className="d-flex">
                <img src="/img/move.svg" alt="move" style={{ width: "16px", height: "16px" }} className="mt-1" />
                <EllipsisTooltipText text={row?.label} className={"ml-3"} />
            </div>
        </td>

        <td className="bbi-executive-export-td-export">
            <div className="not-draggable">
                <SelectBox
                    hideLabel
                    hasEmptySelect={false}
                    isClearable={false}
                    optionsList={EXPORT_SELECT_OPTIONS}
                    value={row.export}
                    handleChange={val => onFieldChange(index, "export", val)}
                    disabled={viewOnly || isClone}
                    className="bbi-executive-export-select export-column"
                    parentClassName="mb-0"
                    skipFormControlClass
                />
            </div>
        </td>

        <td className="bbi-executive-export-td-location">
            <div className="not-draggable">
                <SelectBox
                    hideLabel
                    hasEmptySelect={false}
                    isClearable={false}
                    optionsList={LOCATION_SELECT_OPTIONS}
                    value={row?.location}
                    handleChange={val => onFieldChange(index, "location", val)}
                    disabled={viewOnly || isClone}
                    handleCellFocus={() => {}}
                    className="bbi-executive-export-select location-column"
                    parentClassName="mb-0"
                    skipFormControlClass
                />
            </div>
        </td>
    </>
);

const ExecutiveSummaryTable = ({ onCancel, onSave, viewOnly = false, exportTradeRows = [], isBsi = false, isBai = false }) => {
    const LOCATION_SELECT_OPTIONS = [
        { name: `Above ${isBsi ? "BSI" : isBai ? "BAI" : "BBI"} Table`, id: "above_bbi_table" },
        { name: "In Trades Section", id: "with_trades" }
    ];
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [draftRows, setDraftRows] = useState(() => cloneRows(exportTradeRows));
    const [initialRows] = useState(() => cloneRows(exportTradeRows));
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const isDirty = () => JSON.stringify(initialRows) !== JSON.stringify(draftRows);

    const handleDrag = (e, ui) => {
        setTransform({ left: ui.x, top: ui.y });
    };

    const handleDragEnd = result => {
        if (!result.destination) return;

        setDraftRows(prev => reorderArray(prev, result.source.index, result.destination.index));
    };

    const handleExportFieldChange = (index, field, value) => {
        setDraftRows(prev =>
            prev.map((r, i) => {
                if (i !== index) return r;

                if (field === "export") {
                    return { ...r, export: value };
                }

                if (field === "location") {
                    return { ...r, location: value };
                }

                return r;
            })
        );
    };

    const handleSave = () => {
        onSave?.(cloneRows(draftRows));
    };

    const handleCancelClick = () => {
        if (isDirty()) {
            setShowConfirmModal(true);
        } else {
            onCancel?.();
        }
    };

    const renderConfirmationModal = () => {
        if (!showConfirmModal) return null;

        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading="Do you want to discard changes?"
                        type="cancel"
                        message="This action cannot be reverted. Are you sure you want to cancel?"
                        onNo={() => setShowConfirmModal(false)}
                        onYes={() => {
                            setShowConfirmModal(false);
                            onCancel?.();
                        }}
                    />
                }
                onCancel={() => setShowConfirmModal(false)}
            />
        );
    };

    return (
        <>
            <div
                className="modal modal-region modal-view reco-view-mdl column-setting-modal bbi-config-modal bbi-popup-mod bbi-executive-export-table-overlay"
                style={{ display: "block", pointerEvents: "auto" }}
            >
                <div className="modal-dialog modal-lg bbi-executive-export-table-dialog">
                    <OuterDraggable handle=".draggable" cancel=".not-draggable" onDrag={handleDrag}>
                        <div className="modal-content outerModal_draggable" style={transform}>
                            <div className="modal-header draggable d-flex justify-content-between align-items-center">
                                <h5 className="modal-title">Executive Summary Bands Config (Export Y/N and Location)</h5>
                                <button type="button" class="close" onClick={handleCancelClick}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body bbi-executive-export-table-body">
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <table className="table bbi-executive-export-table mb-0">
                                        <colgroup>
                                            <col className="bbi-executive-export-col-name" />
                                            <col className="bbi-executive-export-col-export" />
                                            <col className="bbi-executive-export-col-location" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className="bbi-executive-export-th bbi-executive-export-th-name">Name</th>
                                                <th className="bbi-executive-export-th bbi-executive-export-th-export">Export</th>
                                                <th className="bbi-executive-export-th bbi-executive-export-th-location">Location</th>
                                            </tr>
                                        </thead>

                                        <Droppable
                                            droppableId="executive-export-trades-dnd"
                                            isDropDisabled={viewOnly}
                                            renderClone={(provided, snapshot, rubric) => {
                                                const row = draftRows[rubric.source.index];

                                                return (
                                                    <table
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="table bbi-executive-export-table bbi-executive-export-table-clone mb-0"
                                                    >
                                                        <colgroup>
                                                            <col className="bbi-executive-export-col-name" />
                                                            <col className="bbi-executive-export-col-export" />
                                                            <col className="bbi-executive-export-col-location" />
                                                        </colgroup>
                                                        <tbody>
                                                            <tr className={snapshot.isDragging ? "bbi-executive-export-row-dragging" : ""}>
                                                                {renderExecutiveSummaryCells({
                                                                    row,
                                                                    index: rubric.source.index,
                                                                    viewOnly,
                                                                    isClone: true,
                                                                    onFieldChange: handleExportFieldChange,
                                                                    LOCATION_SELECT_OPTIONS
                                                                })}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                );
                                            }}
                                        >
                                            {provided => (
                                                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                    {draftRows.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={3} className="text-center">
                                                                No data
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        draftRows.map((row, index) => (
                                                            <Draggable
                                                                key={`exec-row-${row.key}`}
                                                                draggableId={`exec-row-${row.key}`}
                                                                index={index}
                                                                isDragDisabled={viewOnly}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={snapshot.isDragging ? "bbi-executive-export-row-dragging" : ""}
                                                                    >
                                                                        {renderExecutiveSummaryCells({
                                                                            row,
                                                                            index,
                                                                            viewOnly,
                                                                            onFieldChange: handleExportFieldChange,
                                                                            LOCATION_SELECT_OPTIONS
                                                                        })}
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        ))
                                                    )}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </table>
                                </DragDropContext>
                            </div>

                            <div className="btn-sec not-draggable">
                                <div className="text-right btnOtr edit-cmn-btn mt-2 mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btnClr col-md-2 mr-3"
                                        data-dismiss="modal"
                                        onClick={handleCancelClick}
                                    >
                                        Cancel
                                    </button>
                                    {!viewOnly ? (
                                        <button type="button" className={`btn btn-primary col-md-2 pt-2 pb-2 `} onClick={handleSave}>
                                            Save
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </OuterDraggable>
                </div>
            </div>

            {renderConfirmationModal()}
        </>
    );
};

export default ExecutiveSummaryTable;
